// HighlightedAreaSystem: manages time-based highlighted spans on the chart
// and returns random effects when a trade event happens inside a span.

export type HighlightedEffectType =
  | "EXTRA_BUYS"
  | "BONUS_STARTING_MONEY"
  | "BONUS_TIME"
  | "TAX_CURRENT_SUM";

export interface HighlightedEffect {
  readonly type: HighlightedEffectType;
  readonly value: number; // semantic depends on type (see config)
}

interface TimeSpan {
  readonly startTimeMs: number;
  readonly endTimeMs: number;
  readonly effect: HighlightedEffect;
}

// Configuration constants for easy tweaking
export const HIGHLIGHT_CONFIG = {
  // Spawning
  SPAWN_CHANCE_PER_SECOND: 0.3, // probability per real-time second when idle
  SPAN_DURATION_MS_MIN: 1000,
  SPAN_DURATION_MS_MAX: 2000,
  MIN_GAP_BETWEEN_SPANS_MS: 4000,

  // Triggering
  ALLOW_MULTIPLE_TRIGGERS_PER_SPAN: false,
  MAX_TRIGGERS_PER_SPAN: 1 as const,

  // Visuals
  FILL_COLOR: "#44aaff",
  FILL_OPACITY: 0.18,
  BORDER_COLOR: "#44aaff",
  BORDER_WIDTH: 2,

  EFFECT_STYLES: {
    EXTRA_BUYS: {
      fillColor: "#2e86ff",
      borderColor: "#2e86ff",
      textColor: "#ffffff",
    },
    BONUS_STARTING_MONEY: {
      fillColor: "#2ecc71",
      borderColor: "#2ecc71",
      textColor: "#ffffff",
    },
    BONUS_TIME: {
      fillColor: "#9b59b6",
      borderColor: "#9b59b6",
      textColor: "#ffffff",
    },
    TAX_CURRENT_SUM: {
      fillColor: "#e74c3c",
      borderColor: "#e74c3c",
      textColor: "#ffffff",
    },
  } as const,

  // Effects pool and values
  EFFECT_WEIGHTS: {
    EXTRA_BUYS: 1,
    BONUS_STARTING_MONEY: 1,
    BONUS_TIME: 1,
    TAX_CURRENT_SUM: 1,
  } as const,
  EFFECT_VALUES: {
    EXTRA_BUYS: 5, // +5 buys
    BONUS_STARTING_MONEY: 0.05, // +5% of starting money
    BONUS_TIME: 15, // +15 seconds
    TAX_CURRENT_SUM: -0.1, // -10% of current sum
  } as const,
} as const;

export class HighlightedAreaSystem {
  private activeSpan: TimeSpan | null = null;
  private lastSpanEndTimeMs = 0;
  private triggersInCurrentSpan = 0;
  private timeSinceLastCheckMs = 0;

  // Update with current time and elapsed milliseconds (scaled by animation speed)
  public update(currentTimeMs: number, deltaMs: number): void {
    // Clear expired span
    if (this.activeSpan && currentTimeMs > this.activeSpan.endTimeMs) {
      this.activeSpan = null;
      this.triggersInCurrentSpan = 0;
      this.lastSpanEndTimeMs = currentTimeMs;
    }

    // If a span is active, nothing else to do
    if (this.activeSpan) return;

    // Respect minimum gap
    if (
      currentTimeMs - this.lastSpanEndTimeMs <
      HIGHLIGHT_CONFIG.MIN_GAP_BETWEEN_SPANS_MS
    ) {
      return;
    }

    // Accumulate time and roll per second equivalent probability
    this.timeSinceLastCheckMs += deltaMs;
    const seconds = Math.floor(this.timeSinceLastCheckMs / 1000);
    if (seconds <= 0) return;
    this.timeSinceLastCheckMs -= seconds * 1000;

    // Roll once per accumulated second
    for (let i = 0; i < seconds; i++) {
      if (Math.random() < HIGHLIGHT_CONFIG.SPAWN_CHANCE_PER_SECOND) {
        const duration = this.randomInRange(
          HIGHLIGHT_CONFIG.SPAN_DURATION_MS_MIN,
          HIGHLIGHT_CONFIG.SPAN_DURATION_MS_MAX
        );
        const effect = this.randomEffect();
        this.activeSpan = {
          startTimeMs: currentTimeMs,
          endTimeMs: currentTimeMs + duration,
          effect,
        };
        this.triggersInCurrentSpan = 0;
        break;
      }
    }
  }

  public hasActiveSpan(): boolean {
    return !!this.activeSpan;
  }

  // Returns effect if the event is inside the active span and triggering is allowed
  public consumeEffectIfTriggered(
    eventTimeMs: number
  ): HighlightedEffect | null {
    if (!this.activeSpan) return null;
    const { startTimeMs, endTimeMs, effect } = this.activeSpan;
    const inside = eventTimeMs >= startTimeMs && eventTimeMs <= endTimeMs;
    if (!inside) return null;

    if (!HIGHLIGHT_CONFIG.ALLOW_MULTIPLE_TRIGGERS_PER_SPAN) {
      if (
        this.triggersInCurrentSpan >= HIGHLIGHT_CONFIG.MAX_TRIGGERS_PER_SPAN
      ) {
        return null;
      }
    }

    this.triggersInCurrentSpan += 1;
    return effect;
  }

  // Draw the highlighted span within the current visible time window
  public drawOverlay(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    padding: number,
    chartWidth: number,
    chartHeight: number,
    visibleStartTimeMs: number,
    visibleEndTimeMs: number
  ): void {
    if (!this.activeSpan) return;
    const span = this.activeSpan;

    const timeRange = Math.max(1, visibleEndTimeMs - visibleStartTimeMs);
    const clamp = (v: number, min: number, max: number) =>
      Math.max(min, Math.min(max, v));

    // Compute x positions (may be partially outside visible range)
    const xStartRatio = (span.startTimeMs - visibleStartTimeMs) / timeRange;
    const xEndRatio = (span.endTimeMs - visibleStartTimeMs) / timeRange;

    const xStart = padding + xStartRatio * chartWidth;
    const xEnd = padding + xEndRatio * chartWidth;

    // Early exit if completely outside
    if (xEnd < padding || xStart > padding + chartWidth) return;

    const drawX = clamp(xStart, padding, padding + chartWidth);
    const drawW = clamp(xEnd, padding, padding + chartWidth) - drawX;
    if (drawW <= 0) return;

    // Fill
    const style = HIGHLIGHT_CONFIG.EFFECT_STYLES[span.effect.type] ?? {
      fillColor: HIGHLIGHT_CONFIG.FILL_COLOR,
      borderColor: HIGHLIGHT_CONFIG.BORDER_COLOR,
      textColor: "#ffffff",
    };

    ctx.save();
    ctx.globalAlpha = HIGHLIGHT_CONFIG.FILL_OPACITY;
    ctx.fillStyle = style.fillColor;
    ctx.fillRect(drawX, padding, drawW, chartHeight);
    ctx.restore();

    // Border
    ctx.save();
    ctx.strokeStyle = style.borderColor;
    ctx.lineWidth = HIGHLIGHT_CONFIG.BORDER_WIDTH;
    ctx.strokeRect(drawX, padding, drawW, chartHeight);
    ctx.restore();

    // Label (only if there is enough width to display nicely)
    const label = this.formatEffectLabel(span.effect);
    if (label && drawW >= 48) {
      ctx.save();
      ctx.font = "bold 12px monospace";
      ctx.textBaseline = "top";
      ctx.textAlign = "left";
      const textWidth = ctx.measureText(label).width;
      const padX = 6;
      const padY = 4;
      const boxW = textWidth + padX * 2;
      const boxH = 16 + padY * 2;
      const boxX = Math.max(
        drawX + 4,
        Math.min(drawX + drawW - boxW - 4, drawX + 8)
      );
      const boxY = padding + 6;
      // Background box for readability
      ctx.globalAlpha = 0.8;
      ctx.fillStyle = "#000000";
      ctx.fillRect(boxX, boxY, boxW, boxH);
      ctx.globalAlpha = 1;
      ctx.fillStyle = style.textColor;
      ctx.fillText(label, boxX + padX, boxY + padY + 2);
      ctx.restore();
    }
  }

  private randomEffect(): HighlightedEffect {
    const weights = HIGHLIGHT_CONFIG.EFFECT_WEIGHTS;
    const entries: Array<[HighlightedEffectType, number]> = [
      ["EXTRA_BUYS", weights.EXTRA_BUYS],
      ["BONUS_STARTING_MONEY", weights.BONUS_STARTING_MONEY],
      ["BONUS_TIME", weights.BONUS_TIME],
      ["TAX_CURRENT_SUM", weights.TAX_CURRENT_SUM],
    ];
    const total = entries.reduce((sum, [, w]) => sum + w, 0);
    let roll = Math.random() * total;
    for (const [type, w] of entries) {
      if ((roll -= w) <= 0) {
        return { type, value: HIGHLIGHT_CONFIG.EFFECT_VALUES[type] };
      }
    }
    // Fallback (should not happen)
    return {
      type: "EXTRA_BUYS",
      value: HIGHLIGHT_CONFIG.EFFECT_VALUES.EXTRA_BUYS,
    };
  }

  private randomInRange(min: number, max: number): number {
    return Math.floor(min + Math.random() * (max - min));
  }

  private formatEffectLabel(effect: HighlightedEffect): string {
    switch (effect.type) {
      case "EXTRA_BUYS":
        return `+${Math.max(0, Math.floor(effect.value))} buys`;
      case "BONUS_STARTING_MONEY":
        return `+${Math.round(effect.value * 100)}% start`;
      case "BONUS_TIME":
        return `+${Math.max(0, Math.floor(effect.value))}s time`;
      case "TAX_CURRENT_SUM":
        return `-${Math.round(Math.abs(effect.value) * 100)}% tax`;
      default:
        return "";
    }
  }
}
